/**
 * Course-path pills plus program select, used in student/tutor forms.
 * Visibility and defaults come from the programs list (not hardcoded).
 */

import * as React from 'react';
import { Briefcase } from 'lucide-react';
import type { Program } from '@/types/program.types';
import { TogglePill } from '@/components/forms/TogglePill';
import { COURSE_PATH_TYPE_CONFIG } from '@/constants/shared-catalog';
import {
  filterProgramsByCoursePath,
  isProgramCascadePathValid,
  resolveCoursePathFromSelectedPrograms,
  resolveProgramCascade,
  type ProgramCascadePath,
} from '@/utils/program-cascade-utils';

/** Course-path + program fields driven by the programs list. */
interface ProgramCascadeFieldsProps {
  programs: Program[];
  value: {
    tutoringType?: string;
    coursePath?: string;
    programs?: string[];
  };
  onChange: (field: string, value: unknown) => void;
  showTutoringType?: boolean;
  /** When true, the support-path pills are disabled (non-interactive). */
  disabledCoursePath?: boolean;
}

/** Label for the catch-all course-path pill. */
const ALL_PATH_LABEL = 'TOUS';

/** Display label for a course-path pill, including the all-paths option. */
function coursePathPillLabel(path: ProgramCascadePath): string {
  if (path === 'all') return ALL_PATH_LABEL;
  return COURSE_PATH_TYPE_CONFIG[path].label;
}

/** Cascading program, school-year, and specialty fields. */
export const ProgramCascadeFields: React.FC<ProgramCascadeFieldsProps> = ({
  programs,
  value,
  onChange,
  showTutoringType = true,
  disabledCoursePath = false,
}) => {
  const resolution = React.useMemo(() => resolveProgramCascade(programs), [programs]);
  const catalogPath = resolveCoursePathFromSelectedPrograms(programs, value.programs);
  const storedPathValid = isProgramCascadePathValid(value.coursePath, resolution.pills);
  const effectivePath =
    catalogPath ?? (storedPathValid ? value.coursePath : resolution.defaultPath);
  const showPrograms = Boolean(effectivePath);

  React.useEffect(() => {
    if (showTutoringType && !value.tutoringType) {
      onChange('tutoringType', 'remote');
    }
  }, [showTutoringType, value.tutoringType, onChange]);

  React.useEffect(() => {
    if (catalogPath) {
      if (value.coursePath !== catalogPath) onChange('coursePath', catalogPath);
      return;
    }
    if (!resolution.defaultPath) return;
    if (isProgramCascadePathValid(value.coursePath, resolution.pills)) return;
    onChange('coursePath', resolution.defaultPath);
  }, [catalogPath, resolution.defaultPath, resolution.pills, value.coursePath, onChange]);

  /** Changing course path clears selected programs (they belong to the previous path). */
  const handleCoursePathChange = (path: ProgramCascadePath) => {
    onChange('coursePath', path);
    onChange('programs', []);
  };

  /** Radio-style toggle: selecting a program replaces the current one (single-select). */
  const handleProgramToggle = (programId: string) => {
    const isSelected = value.programs?.includes(programId);
    onChange('programs', isSelected ? [] : [programId]);
  };

  const filteredPrograms = filterProgramsByCoursePath(programs, effectivePath);

  React.useEffect(() => {
    if (filteredPrograms.length === 1 && !value.programs?.length) {
      onChange('programs', [filteredPrograms[0].id]);
    }
  }, [filteredPrograms.length, filteredPrograms[0]?.id, value.programs?.length, onChange]);

  function renderCoursePathPill(path: ProgramCascadePath): React.ReactNode {
    function handleClick(): void {
      if (!disabledCoursePath) handleCoursePathChange(path);
    }
    return (
      <TogglePill
        key={path}
        label={coursePathPillLabel(path)}
        active={effectivePath === path}
        onClick={handleClick}
        disabled={disabledCoursePath}
        className="flex-1"
      />
    );
  }

  function renderProgramPill(program: Program): React.ReactNode {
    function handleClick(): void {
      handleProgramToggle(program.id);
    }
    return (
      <TogglePill
        key={program.id}
        label={program.name || program.code}
        active={Boolean(value.programs?.includes(program.id))}
        onClick={handleClick}
      />
    );
  }

  return (
    <div className="form-section">
      <h4 className="form-section-title">
        <Briefcase className="w-4 h-4" />
        Programme
      </h4>

      <div className="space-y-6">
        {resolution.showCoursePath && (
          <div>
            <p className="form-label form-label-required">Parcours d'accompagnement</p>
            <p className="text-xs text-muted-foreground mb-2">
              Classique et Club de sport sont nos parcours les plus demandés
            </p>
            <div className="flex gap-2 mt-2">{resolution.pills.map(renderCoursePathPill)}</div>
          </div>
        )}

        {showPrograms && (
          <div>
            <p className="form-label form-label-required">Programme d'accompagnement</p>
            <p className="text-xs text-muted-foreground mb-2">
              Les programmes sont susceptibles d'évoluer.
            </p>
            <div className="flex flex-wrap gap-2 mt-2">
              {filteredPrograms.map(renderProgramPill)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
